import { useState } from 'react';
import { useMutation, queryOptions, type QueryClient } from '@tanstack/react-query';
import { client } from '@blazeteams/sdk/client';
import { useVoicePresenceStore } from './voice-presence-store';
import { useVoiceSessionStore } from './voice-session-store';
import { captureVoiceOwner, voiceAuthorization, voiceKey, ownsVoiceKey, requireVoiceOwner, isCurrentVoiceOwner, type VoiceSession } from './voice-owner';
import { VoiceAPIError, voiceUUID, readVoiceObservation, readVoiceToken, readVoiceProblem, type VoiceTokenResponse } from './voice-contract';
export type { VoiceTokenResponse } from './voice-contract';

async function readOwnedVoice(owner: VoiceSession, channel: string, operation: 'participants' | 'token', signal?: AbortSignal) {
  requireVoiceOwner(owner, signal); if (!voiceUUID(channel)) throw new VoiceAPIError('bad_input');
  const authorization = voiceAuthorization(owner); let result;
  try { result = await client.get<unknown>({ url: '/voice/channels/{channelId}/' + operation, path: { channelId: channel }, signal, headers: { Authorization: authorization, 'Accept-Language': owner.language } }); }
  catch { requireVoiceOwner(owner, signal); throw new VoiceAPIError('read_failed'); }
  requireVoiceOwner(owner, signal);
  if (result.error || result.response?.status !== 200) throw readVoiceProblem(result.error, result.response?.status ?? 0);
  return result.data;
}
export async function readOwnedVoiceToken(owner: VoiceSession, channel: string, signal?: AbortSignal) {
  return readVoiceToken(await readOwnedVoice(owner, channel, 'token', signal), owner, channel);
}
export const voiceQueries = {
  participants: (channel: string, owner = captureVoiceOwner()) => queryOptions({
    queryKey: voiceKey(channel, owner), enabled: voiceUUID(channel) && voiceUUID(owner.user) && voiceUUID(owner.tenant), retry: false, staleTime: 30_000, refetchInterval: 30_000,
    queryFn: async ({ signal }) => {
      requireVoiceOwner(owner, signal); useVoicePresenceStore.getState().reading(channel);
      try {
        const value = readVoiceObservation(await readOwnedVoice(owner, channel, 'participants', signal), owner, channel);
        requireVoiceOwner(owner, signal); useVoicePresenceStore.getState().observe(channel, value); return value;
      } catch (error) {
        requireVoiceOwner(owner, signal);
        const denied = error instanceof VoiceAPIError && [401, 403, 404].includes(error.status);
        useVoicePresenceStore.getState().failed(channel, error instanceof VoiceAPIError ? error.key : 'read_failed', denied);
        if (denied && useVoiceSessionStore.getState().channelId === channel) useVoiceSessionStore.getState().leave();
        throw error;
      }
    },
  }),
};
export async function reconcileVoice(qc: QueryClient, channel: string, owner = captureVoiceOwner()) {
  requireVoiceOwner(owner); await qc.cancelQueries({ queryKey: voiceKey(channel, owner) }); requireVoiceOwner(owner);
  await qc.fetchQuery({ ...voiceQueries.participants(channel, captureVoiceOwner()), staleTime: 0 }); requireVoiceOwner(owner);
}
export function reconcileVisibleVoice(qc: QueryClient) {
  const owner = captureVoiceOwner();
  return qc.invalidateQueries({ predicate: query => ownsVoiceKey(query.queryKey, owner) });
}
export function useVoiceChannelToken() {
  const [view] = useState(()=>({owner:captureVoiceOwner(), sessionRevision:useVoiceSessionStore.getState().revision}));
  const requireIntent = () => { requireVoiceOwner(view.owner); if (view.sessionRevision!==useVoiceSessionStore.getState().revision) throw new DOMException('Aborted','AbortError'); };
  const currentIntent = () => isCurrentVoiceOwner(view.owner) && view.sessionRevision===useVoiceSessionStore.getState().revision;
  const mutation = useMutation({ retry: false, gcTime: 0, mutationFn: async (channel: string) => { requireIntent(); const data=await readOwnedVoiceToken(captureVoiceOwner(), channel); requireIntent(); return data; } });
  const mutate = (channel: string, options?: {onSuccess?:(data:VoiceTokenResponse)=>unknown;onError?:(error:unknown)=>unknown;onSettled?:(data:VoiceTokenResponse|undefined,error:unknown)=>unknown}) => {
    if (!currentIntent()) return;
    const owner = captureVoiceOwner();
    void mutation.mutateAsync(channel).then(data => {
      if (isCurrentVoiceOwner(owner) && currentIntent()) { options?.onSuccess?.(data); options?.onSettled?.(data, null); }
    }, error => {
      if (isCurrentVoiceOwner(owner) && currentIntent()) { options?.onError?.(error); options?.onSettled?.(undefined, error); }
    });
  };
  return { ...mutation, mutate };
}
